import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { portalUrls } from '@/config';
import { useAuth } from '@/lib/auth-context';
import { ctaLabelForAccess, planAccessForProduct } from '@/lib/plan-access';

/** Live $197 Payment Link — fallback if API checkout fails. */
export const STRIPE_URL =
  process.env.REACT_APP_STRIPE_LINK_POA || 'https://buy.stripe.com/5kQ9AT8ob0Au0wReNH5os01';

/**
 * Big red CTA — same auth-aware checkout as protego-landing:
 * guest → portal signup + pending checkout; logged in → Stripe Checkout Session (plan=poa).
 */
export default function CTAButton({
  children = 'Get My Plan of Action — $197',
  subtext = null,
  testId,
  className = '',
  size = 'lg',
}) {
  const { beginCheckout, user, isAuthenticated } = useAuth();
  const [starting, setStarting] = useState(false);
  const access = planAccessForProduct('poa', isAuthenticated ? user : null);
  const label = ctaLabelForAccess(access, children);
  const isOwned = access === 'current' || access === 'included';

  const padding =
    size === 'lg' ? 'px-5 sm:px-10 py-4 sm:py-6 text-base sm:text-xl' : 'px-4 py-3 text-sm sm:text-base';

  const baseClass = `group relative inline-flex items-center justify-center gap-2 sm:gap-3 ${padding} font-black tracking-tight bg-[#F04632] hover:bg-[#D93D2A] text-white rounded-xl pulse-glow uppercase text-center max-w-full disabled:cursor-wait disabled:opacity-70`;

  if (isOwned) {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        <motion.a
          href={portalUrls.dashboard}
          data-testid={testId}
          whileHover={{ y: -2, scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className={baseClass}
        >
          <span>{label} · Open dashboard</span>
          <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={3} />
        </motion.a>
        {subtext && (
          <p className="mt-3 max-w-md text-center text-xs tracking-wide text-white/50 sm:text-sm">{subtext}</p>
        )}
      </div>
    );
  }

  const handleClick = async () => {
    if (starting) return;
    setStarting(true);
    try {
      await beginCheckout({ plan: 'poa', fallbackUrl: STRIPE_URL });
    } catch {
      window.location.assign(STRIPE_URL);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <motion.button
        type="button"
        data-testid={testId}
        onClick={handleClick}
        disabled={starting}
        whileHover={{ y: -2, scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className={baseClass}
      >
        <span>{starting ? 'Starting checkout…' : label}</span>
        <ArrowRight
          className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 sm:h-6 sm:w-6"
          strokeWidth={3}
        />
      </motion.button>
      {subtext && (
        <p className="mt-3 max-w-md text-center text-xs tracking-wide text-white/50 sm:text-sm">{subtext}</p>
      )}
    </div>
  );
}
